import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FieldExpression, NodeExpression, RangeExpression, toString as luceneToString } from 'lucene';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { DATETIME_FORMAT_PATTERN } from '../../models/filter-formats';
import { News } from '../../models/news-model';
import { NewsService } from '../../services/news.service';
import { TranslationService } from '../../services/translation.service';
import { OrderBy, OrderByDirection } from '../../utils/constant';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'rowCreationDate';

@Component({
	selector: 'copo-news-alert',
	templateUrl: './news-alert.component.html',
	styleUrl: './news-alert.component.css',
	encapsulation: ViewEncapsulation.Emulated,

	imports: [NgIf, MatButtonModule, MatIconModule, RouterLink, TranslateModule]
})
export class NewsAlertComponent implements OnDestroy {
	hideAlert = false;
	filter?: string;
	alertTitle: string | undefined;
	alertSubscription?: Subscription;
	alertContentDescription: string | undefined;
	limit = 1;
	alertList: News[] = [];
	newsFields = <const> [
		'id',
		'type',
		'from',
		'to',
		'audiences.members',
		'newsContents',
		'newsContents.newsAttachments',
		'services',
		'audiences',
		'rowCreationBy',
		'rowCreationDate'
	];

	currentDate = moment(Date.now()).format(DATETIME_FORMAT_PATTERN);
	constructor(
		private readonly newsService: NewsService,
		private readonly translationService: TranslationService
	) {
		this.getAlerts();
	}

	getAlerts() {
		this.filter = this.createLuceneFilter();
		this.alertSubscription = this.newsService.getNews(
			this.newsFields,
			this.filter,
			0,
			this.limit,
			new OrderBy(SORT_BY, SORT_WAY)
		).subscribe(alerts => {
			this.alertList = alerts;
			for (const item of this.alertList) {
				let currentLanguageItem = item.contents
					.find(content => content.languageCode === this.translationService.currentLanguage);
				if (!currentLanguageItem) {
					currentLanguageItem = item.contents
						.find(content => content.languageCode === 'en');
				}
				this.alertTitle = currentLanguageItem?.title;
				this.alertContentDescription = this.convertAlertContentToHTML(currentLanguageItem?.content);
			}
		});
	}

	closeAlert() {
		this.hideAlert = true;
	}

	ngOnDestroy() {
		if (this.alertSubscription) {
			this.alertSubscription.unsubscribe();
		}
	}

	convertAlertContentToHTML(text: string | undefined): string | undefined {
		const urlPattern = /(https?:\/\/\S+)/g;
		// eslint-disable-next-line sonarjs/slow-regex
		const emailPattern = /([\w.-]+@[\w.-]+\.[\w-]+)/g;
		return text?.replace(
			urlPattern,
			'<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
		)
			.replace(emailPattern, '<a href="mailto:$1">$1</a>');
	}

	private createLuceneFilter() {
		const typeFilter: FieldExpression = {
			field: 'type',
			term: 'alert',
			quoted: true
		};
		const fromDateFilter: RangeExpression = {
			field: 'from',
			term_min: '*',
			term_max: this.currentDate,
			inclusive: 'both'
		};
		const toDateFilter: RangeExpression = {
			field: 'to',
			term_min: this.currentDate,
			term_max: '*',
			inclusive: 'both'
		};
		const dateFilter: NodeExpression = {
			left: fromDateFilter,
			operator: 'AND',
			right: toDateFilter,
			parenthesized: true
		};
		const filterNode: NodeExpression = {
			left: dateFilter,
			operator: 'AND',
			right: typeFilter,
			parenthesized: true
		};
		return luceneToString(filterNode);
	}
}
